import { useState, useEffect } from 'react';
import { PageLayoutsContainer } from '@wajeez-web/components/layouts';
import { Button, InputSelect, PopOver } from '@wajeez-web/components/ui';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Server } from '@wajeez-web/core/api-base';
import {
  ContentEntityActions,
  AuthorsActions,
  useContentSelector,
  contentEntityDataType,
} from '@wajeez-web/stores/content';
import { useTranslation } from 'react-i18next';
import { SuperAdminSystemPermissions } from '@wajeez-web/core/types';
import { PlusIcon } from '@heroicons/react/solid';
import AssignPlansModal from '../components/AssignPlansModal';
import { HandleTableData } from '@wajeez-web/components/data-table';
import AssignAuthorsModal from '../components/AssignAuthorsModal';
import { useCommonDispatch } from '@wajeez-web/stores/common';
import { DotsHorizontalIcon } from '@heroicons/react/outline';
import { DataTable, Dropdown, CheckBoxType } from '@wajeez-web/components/mui';
import { DataTableWrapper } from '@wajeez-web/components/helpers';

interface ContentEntityWithAuthorsDatatableExtended
  extends Server.ContentEntityWithAuthorsDatatable {
  totalCount: number | undefined;
}

const AuthorsContentEntity = () => {
  const { id } = useParams<{ id: string }>();

  const dispatch = useCommonDispatch();

  const navigate = useNavigate();

  const [type, setType] = useState('');
  const [authorModalState, setAuthorModalState] = useState({
    open: false,
    selected: '',
  });
  const [plansModalState, setplansModalState] = useState({
    open: false,
    selected: '',
  });
  const [contentfiltertypesOptions, setContentFilterTypeOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [shouldClosePopover, setShouldClosePopover] = useState(false);

  const { t } = useTranslation();

  const authorData = useContentSelector((state) => state.authors.details);

  const contentEntityTypes = useContentSelector(
    (state) => state.contentEntityType.lookups
  );

  const authorContent = useContentSelector(
    (state) => state.contentEntity.authorContent
  );
  const authorContentStatus = useContentSelector(
    (state) => state.contentEntity.authorContentStatus
  );

  const authorContentExtended =
    authorContent as ContentEntityWithAuthorsDatatableExtended;

  const {
    tablePreferances,
    setTablePreferences,
    getData,
    isTableReferencesLoading,
  } = HandleTableData(
    'authorContent',
    ContentEntityActions.getContentEntitiesByAuthorAsync,
    undefined,
    { authorId: Number(id) },
    [{ property: 32, orderByDirection: 1 }]
  );

  useEffect(() => {
    contentEntityTypes &&
      setContentFilterTypeOptions(
        contentEntityTypes
          .filter((item) => item.requireAuthor)
          .map((item) => ({
            label: item.displayNamePlural || '',
            value: item.name || '',
          }))
      );
  }, [contentEntityTypes]);

  useEffect(() => {
    if (contentfiltertypesOptions.length) {
      setType(contentfiltertypesOptions[0].value);
    }
  }, [contentfiltertypesOptions]);

  useEffect(() => {
    dispatch(AuthorsActions.getAuthorByIdAsync(Number(id)));
  }, []);

  return (
    <div className="h-full">
      <PageLayoutsContainer
        title={t(`pageHeader.${'Author Content'}`, '') + ' ' + authorData?.name}
        withoutRightPadding={true}
        endSideComponent={[
          {
            element: (
              // TODO : asign the author to the content
              <Button
                title={`Add ${type.toLowerCase()}`}
                Icon={PlusIcon}
                onClick={() => {
                  navigate(`/content/type/${type}/create/0`);
                }}
                bg="primary"
                color="white"
              />
            ),
            permission: [SuperAdminSystemPermissions.contentEntities.add],
          },
        ]}
        breadCrumbsLinks={[
          { title: 'Authors', link: '/authors' },
          { title: 'Content' },
        ]}
      >
        <div className="h-full min-h-inner-screen">
          <DataTableWrapper
            data={authorContentExtended as contentEntityDataType}
            onHeaderManage={(header) => {
              const flags = header
                ?.filter((h) => h.selected)
                .reduce(
                  (previousValue: number, currentValue: CheckBoxType) =>
                    previousValue + Number(currentValue.value),
                  0
                );

              tablePreferances &&
                flags &&
                setTablePreferences({
                  ...tablePreferances,
                  flags: flags + 1,
                });
            }}
            onPaginationChange={(pagination) => {
              tablePreferances &&
                setTablePreferences({
                  ...tablePreferances,
                  pageNumber: pagination.pageNumber,
                  pageSize: pagination.pageSize,
                });
            }}
            sort={tablePreferances?.sortingOperations?.[0]}
            onSort={(sort) => {
              tablePreferances &&
                setTablePreferences({
                  ...tablePreferances,
                  sortingOperations: [sort],
                });
            }}
            onFilter={(filters, pagination) => {
              tablePreferances &&
                setTablePreferences({
                  ...tablePreferances,
                  pageNumber: pagination.pageNumber,
                  filteringOperations: filters.map((f) => {
                    return {
                      property: Number(f.key),
                      cretiria: f.value,
                      comparisonType: f.operator,
                    };
                  }),
                });
            }}
            rowActions={(row) => {
              return [
                {
                  title: 'Edit',
                  permission: SuperAdminSystemPermissions.contentEntities.edit,
                  endpoint: ``,
                  type: 'request',
                  action: () => {
                    navigate(`content/type/${type}/${row[1]}/0`);
                  },
                },
                {
                  title: 'Update plans',
                  permission: SuperAdminSystemPermissions.contentEntities.plans,
                  endpoint: '',
                  type: 'request',
                  action: () => {
                    setplansModalState({
                      selected: row[1] as string,
                      open: true,
                    });
                  },
                },
              ];
            }}
            isLoading={authorContentStatus === 'loading'}
            preferences={tablePreferances || undefined}
            setPreferences={setTablePreferences}
            columnsSelectors={(r) => {
              return {
                '8': (
                  <Link to={`content/type/${type}/${r[1]}/0`}>
                    <div className="text-[#38A7B4] font-medium">{r[8]}</div>
                  </Link>
                ),
              };
            }}
            defaultFilters={[
              {
                key: '2',
                dataType: Server.DataType.String,
                label: 'Content Type',
                operator: Server.ComparisonType.Equal,
                fieldMeta: {
                  fieldType: Server.FieldType.Select,
                  fieldOptions: {
                    selectOptions: contentfiltertypesOptions,
                  },
                },
                onChange: (value) => {
                  setType(value);
                },
              },
            ]}
          />
          {authorModalState.open && (
            <AssignAuthorsModal
              isOpen={authorModalState.open}
              contentId={authorModalState.selected}
              onClose={() => {
                setAuthorModalState({ selected: '', open: false });
              }}
            />
          )}
          {plansModalState.open && (
            <AssignPlansModal
              contentEntityId={plansModalState.selected}
              isOpen={plansModalState.open}
              onClose={() => setplansModalState({ selected: '', open: false })}
              onSubmit={async (data) => {
                const res = await dispatch(
                  ContentEntityActions.updateContentPlansAsync({
                    contentEntityId: plansModalState.selected,
                    planIds: data || [],
                  })
                );
                if (res.meta.requestStatus === 'fulfilled') {
                  setplansModalState({ selected: '', open: false });
                }
              }}
            />
          )}
        </div>
      </PageLayoutsContainer>
    </div>
  );
};

export default AuthorsContentEntity;
